import React from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend, ResponsiveContainer
} from "recharts";
import LocalLoaderDark from "../../components/localLoaderDark";

function CustomToolTip({ active, payload }) {
    if (active && payload && payload.length) {
        return (
            <div className={'text-dutch_white-800 p-1 max-w-[300px] overflow text-sm rounded bg-paynes_gray'}>
                <p className={'text-dutch_white-200'}>{new Date(payload.map(item => item.payload.name)[0]).toDateString()}</p>
            </div>
        )
    }

    return null;
}

// function CustomXTick({ active, payload, label }) {
//     if (active && payload && payload.length) {
//         console.log(payload)
//         return (
//             <div className={'bg-dutch_white-400 text-dutch_white-800 p-2 max-w-[170px] overflow text-lg'}>
//                 <p>{label}</p>
//                 <br />
//                 {payload.map(item => (<p>{item.dataKey}: {item.value}</p>))}
//             </div>
//         )
//     }
//
//     return null;
// }


export default function HistoryBarChart({ data, assessmentItems }) {
    if (!data || !data.studentData || !data.roster || !data.roster.students) {
        return <LocalLoaderDark />;
    } else {
        const students = data.studentData.filter(s =>
            data.roster.students.includes(s.studentId)
        );

        const assessments = [
            // Active
            ...students.flatMap(student =>
                (student.active[data.prep] || []).map(assessment => ({
                    student,
                    assessment
                }))
            ),

            // Archive
            ...students.flatMap(student =>
                (student.archive[data.prep] || []).map(assessment => ({
                    student,
                    assessment
                }))
            )
        ];

// Final filtering
        const filtered = assessments.filter(
            item =>
                item.assessment.knowShowId === data.standardElement._id.$oid &&
                "dateAssigned" in item.assessment
        );

        const historyBarChartDataConstructor = Object.values(
            filtered.reduce((acc, entry) => {
                const raw = entry.assessment?.dateAssigned?.$date || entry.assessment?.dateAssigned;
                if (!raw) return acc;

                const iso = new Date(raw).toISOString();
                const result = entry.assessment.result;
                const student = entry.student;

                // Initialize bucket
                if (!acc[iso]) {
                    acc[iso] = {
                        name: iso,
                        Correct: 0,
                        Incorrect: 0,
                        Pending: 0,
                        students: {
                            Correct: [],
                            Incorrect: [],
                            Pending: []
                        }
                    };
                }

                const bucket = acc[iso];

                // Classify and store student
                if (
                    (typeof result === "number" && result === 0) ||
                    (typeof result === "boolean" && result === true)
                ) {
                    bucket.Correct++;
                    bucket.students.Correct.push(student);
                } else if (
                    (typeof result === "number" && result > 0) ||
                    (typeof result === "boolean" && result === false)
                ) {
                    bucket.Incorrect++;
                    bucket.students.Incorrect.push(student);
                } else if (result === "pending") {
                    bucket.Pending++;
                    bucket.students.Pending.push(student);
                }

                return acc;
            }, {})
        );


        const TempCustomTooltip = ({ active, payload, label }) => {
            if (!active || !payload?.length) return null;

            const data = payload[0].payload; // full bucket

            return (
                <div className="tooltip bg-columbia_blue-300 rounded p-1">
                    <h4 className={'text-columbia_blue-900 font-bold'}>Date Assigned: {new Date(label).toLocaleDateString()}</h4>

                    {/*<div className={'m-1 bg-columbia_blue-700 text-dutch_white-100 rounded'}>*/}
                    {/*    <strong>Correct:</strong>*/}
                    {/*    <ul>*/}
                    {/*        {data.students.Correct.map(s => (*/}
                    {/*            <li key={s.studentId}>{s.firstName} {s.lastName}</li>*/}
                    {/*        ))}*/}
                    {/*    </ul>*/}
                    {/*</div>*/}

                    <div className={'m-1 bg-burnt_sienna-200 text-columbia_blue-900 rounded'}>
                        <strong>Incorrect:</strong>
                        <ul className={'text-sm'}>
                            {data.students.Incorrect.sort((a, b) => a.lastName.localeCompare(b.lastName)).map(s => (
                                <li key={s.studentId}>{s.firstName} {s.lastName}</li>
                            ))}
                        </ul>
                    </div>

                    <div className={'m-1 bg-platinum-800 text-dutch_white-100 rounded'}>
                        <strong>Pending:</strong>
                        <ul className={'text-sm'}>
                            {data.students.Pending.sort((a, b) => a.lastName.localeCompare(b.lastName)).map(s => (
                                <li key={s.studentId}>{s.firstName} {s.lastName}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            );
        };


        const BarWithBorder = (props) => {
            const { fill, x, y, width, height } = props;
            return (
                <g tabIndex={0} onClick={props.onClick}>
                    <rect x={x} y={y} width={width} height={height} stroke="none" fill={fill} />
                    <rect x={x} y={y} width={width} height={2} stroke="none" fill={'white'} />
                </g>
            );
        };

// Sort by datetime ascending
        historyBarChartDataConstructor.sort(
            (a, b) => new Date(a.name) - new Date(b.name)
        );

        const CustomLabelCheck = ({ x, y, width, height, value }) => {
            return (
                <svg
                    x={x + width / 2 - 10}  // Center the label horizontally relative to the bar's width
                    y={y + height - 28}      // Position the label just above the bottom of the bar
                    width={30}
                    height={30}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="black"
                    strokeWidth="2"
                >{/* Added fill to rectangle */}
                    <path d="M4 12 L8 16 L16 8" stroke="#f6f0e0" strokeWidth="1.5" fill="none"/>
                    {/* Centered the checkmark */}
                </svg>
            );
        };

        const CustomLabelX = ({ x, y, width, height, value }) => {
            return (
                <svg
                    x={x + width / 2 - 10}  // Center the label horizontally relative to the bar's width
                    y={y + height - 20}      // Position the label just above the bottom of the bar
                    width={30}
                    height={30}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="black"
                    strokeWidth="2">
                    <path d="M5 2 L13 10 M13 2 L5 10" stroke="#f6f0e0" strokeWidth="1.5" fill="none"/>
                    {/* Centered the checkmark */}
                </svg>
            );
        };

        const CustomLabelPending = ({ x, y, width, height }) => {
            return (
                <svg
                    x={x + width / 2 - 10}  // Center the label horizontally relative to the bar's width
                    y={y + height - 25}      // Position the label just above the bottom of the bar
                    width={30}
                    height={30}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#5e5e5e"
                    strokeWidth="1">
                    <text
                        x="39%"                 // Center the text horizontally
                        y="70%"                 // Adjust the vertical position to fit above the bar
                        textAnchor="middle"     // Center the text anchor
                        fill="#5e5e5e"            // Set the fill color for the text
                        fontSize="16"           // Set the font size
                    >
                        ?
                    </text>
                </svg>
            );
        };
        if (filtered) {
            return (
                <div className={'w-[95%] mx-auto'}>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                            data={historyBarChartDataConstructor}
                            margin={{
                                top: 20,
                                right: 30,
                                left: 20,
                                bottom: 5
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#eeeeee"/>
                            <XAxis dataKey='name' className={'font-bold'} tick={false}/>
                            <Tooltip cursor={false} content={<TempCustomTooltip/>}/>
                            <Legend/>
                            <Bar dataKey="Correct" stackId="a" fill="#538ea5" shape={<BarWithBorder />} label={CustomLabelCheck}/>
                            <Bar dataKey="Incorrect" stackId="a" fill="#dd6e42" shape={<BarWithBorder />} label={CustomLabelX}/>
                            <Bar dataKey="Pending" stackId="a" fill="#bcbcbc" shape={<BarWithBorder />} label={CustomLabelPending}/>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )
        } else {
            return <LocalLoaderDark />
        }
    }

}
